import type { ZodType } from "zod";
import z from "zod";

import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import { CustomerRoleSchema, type CustomerRole } from "./thalamos.js";
import {
  EmailAddressSchema,
  RoleSchema,
  UserIdSchema,
  UserMembershipSchema,
  type EmailAddressUnbranded,
  type Role,
  type UserIdUnbranded,
  type UserMembershipUnbranded,
} from "./user.js";

/**
 * The membership to grant: a role, and the organisation to hold it at.
 *
 * No user id, because the account is the resource the request is addressed to
 * rather than part of what is being said about it — which is what keeps a grant
 * from being submitted against one account while naming another.
 *
 * `CustomerRole` rather than `Role`, so the administrative membership cannot be
 * granted here at all. The organisation is checked separately by the rules, so
 * the two halves of that guard fail independently.
 *
 * The organisation's display name is deliberately absent: it is the server's to
 * state. See `SetSessionContextRequest`, which drops it for the same reason.
 */
export type GrantMembershipRequestUnbranded = {
  organisationId: OrganisationIdUnbranded;
  role: CustomerRole;
};

export const GrantMembershipRequestSchema = z
  .object({
    organisationId: OrganisationIdSchema,
    role: CustomerRoleSchema,
  })
  .brand<"haven-common:GrantMembershipRequest">() satisfies ZodType<GrantMembershipRequestUnbranded>;

export type GrantMembershipRequest = z.infer<
  typeof GrantMembershipRequestSchema
>;

/**
 * The memberships an account holds.
 *
 * Wrapped in an object rather than returned as a bare array so the response can
 * gain a field without every client's parse breaking — a top-level array has
 * nowhere to put one.
 *
 * The same shape answers a read and a change, so the result of a grant is a set
 * a caller may render as it stands.
 */
export type UserMembershipsUnbranded = {
  memberships: UserMembershipUnbranded[];
};

export const UserMembershipsSchema = z.object({
  memberships: z.array(UserMembershipSchema),
}) satisfies ZodType<UserMembershipsUnbranded>;

export type UserMemberships = z.infer<typeof UserMembershipsSchema>;

/** One membership held at an organisation, and the account holding it. */
export type OrganisationUserMembershipUnbranded = {
  userId: UserIdUnbranded;
  name: string;
  email: EmailAddressUnbranded;
  role: Role;
};

/**
 * Every membership held at an organisation: one row per membership, so an
 * account holding two roles there appears twice.
 */
export type OrganisationUserMembershipsUnbranded = {
  memberships: OrganisationUserMembershipUnbranded[];
};

export const OrganisationUserMembershipSchema = z.object({
  userId: UserIdSchema,
  name: z.string().min(1),
  email: EmailAddressSchema,
  role: RoleSchema,
}) satisfies ZodType<OrganisationUserMembershipUnbranded>;

export const OrganisationUserMembershipsSchema = z.object({
  memberships: z.array(OrganisationUserMembershipSchema),
}) satisfies ZodType<OrganisationUserMembershipsUnbranded>;

export type OrganisationUserMemberships = z.infer<
  typeof OrganisationUserMembershipsSchema
>;
